/**
 * Order Hook using TanStack Query
 * Handles order creation and management with automatic caching
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { OrdersResource } from '../../core/resources/orders';
import { Order, OrderLineItem } from '../../core/utils/order';

import { getGlobalApiClient } from './useApiQuery';

export interface UseOrderQueryOptions {
    orderId?: string;
    enabled?: boolean;
}

export interface UseOrderQueryResult {
    // Query data
    order: Order | undefined;
    isLoading: boolean;
    error: Error | null;

    // Actions
    createOrder: (checkoutSessionId: string) => Promise<{ success: boolean; order?: Order; error?: string }>;
    updateOrderStatus: (status: string) => Promise<{ success: boolean; error?: string }>;
    addOrderItems: (items: Omit<OrderLineItem, 'id'>[]) => Promise<{ success: boolean; error?: string }>;
    refresh: () => void;
}

export function useOrderQuery(options: UseOrderQueryOptions = {}): UseOrderQueryResult {
    const { orderId, enabled = true } = options;

    // Create orders resource client
    const ordersResource = useMemo(() => {
        try {
            return new OrdersResource(getGlobalApiClient());
        } catch (error) {
            throw new Error('Failed to initialize orders resource: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, []);

    // Main order query
    const {
        data: order,
        isLoading,
        error,
        refetch,
    } = useQuery<Order>({
        queryKey: ['order', orderId],
        queryFn: () => ordersResource.getOrder(orderId!),
        enabled: enabled && !!orderId,
        staleTime: 60_000, // 1 minute
        refetchOnWindowFocus: false,
    });

    // Create order mutation
    const createMutation = useMutation<
        { success: boolean; order?: Order; error?: string },
        Error,
        { checkoutSessionId: string }
    >({
        mutationFn: ({ checkoutSessionId }) => {
            return ordersResource.createOrder(checkoutSessionId);
        },
        onSuccess: () => {
            // Order created successfully - no specific cache invalidation needed
        },
    });

    // Update order status mutation
    const updateStatusMutation = useMutation<
        { success: boolean; error?: string },
        Error,
        { status: string }
    >({
        mutationFn: ({ status }) => {
            if (!orderId) {
                throw new Error('No order ID available');
            }

            return ordersResource.updateOrderStatus(orderId, status);
        },
        onSuccess: () => {
            // Refetch order data
            void refetch();
        },
    });

    // Add order items mutation
    const addItemsMutation = useMutation<
        { success: boolean; error?: string },
        Error,
        { items: Omit<OrderLineItem, 'id'>[] }
    >({
        mutationFn: ({ items }) => {
            if (!orderId) {
                throw new Error('No order ID available');
            }

            return ordersResource.addOrderItems(orderId, items);
        },
        onSuccess: () => {
            // Refetch order data
            void refetch();
        },
    });

    return {
        // Query data
        order,
        isLoading,
        error,

        // Actions
        createOrder: async (checkoutSessionId: string) => {
            try {
                return await createMutation.mutateAsync({ checkoutSessionId });
            } catch (err) {
                const error = err instanceof Error ? err : new Error('Failed to create order');
                return { success: false, error: error.message };
            }
        },
        updateOrderStatus: async (status: string) => {
            try {
                return await updateStatusMutation.mutateAsync({ status });
            } catch (err) {
                const error = err instanceof Error ? err : new Error('Failed to update order status');
                return { success: false, error: error.message };
            }
        },
        addOrderItems: async (items: Omit<OrderLineItem, 'id'>[]) => {
            try {
                return await addItemsMutation.mutateAsync({ items });
            } catch (err) {
                const error = err instanceof Error ? err : new Error('Failed to add order items');
                return { success: false, error: error.message };
            }
        },
        refresh: () => void refetch(),
    };
}
